import type { QueryClient } from '@tanstack/react-query';
import { segmentOutIntegrationKeys, type SegmentOutIntegration } from './entity';

/**
 * Imperative cache ops for the Segment source connector record.
 *
 * This slot serves the config page and its two route views. It is **not** what
 * the integrations directory and the connections overview read: those take the
 * same underlying row in its legacy `segdotcom` shape, under
 * `statusKey(workspaceId, 'segdotcom')` in `services/db/integration`.
 *
 * So a save or a disconnect here does not move their "connected" badge; that
 * slot carries a 60s `staleTime` and refreshes on its own. Every connector
 * behaves this way — none of them invalidate the status slot — so fixing it
 * belongs across all of them rather than here.
 */
const detailKey = (workspaceId: number) => segmentOutIntegrationKeys.detail(workspaceId, 'segment-out');

export function setSegmentOutIntegrationCache(
  qc: QueryClient,
  workspaceId: number,
  integration: SegmentOutIntegration,
): void {
  qc.setQueryData(detailKey(workspaceId), integration);
}

/**
 * A disconnected connector is "connected: no", which is `null` — not an absent
 * slot. Writing `null` keeps every observer of this slot on the same answer
 * without a refetch; removing the slot would leave observers refetching
 * a record that is gone.
 */
export function clearSegmentOutIntegrationCache(qc: QueryClient, workspaceId: number): void {
  qc.setQueryData(detailKey(workspaceId), null);
}
